import axios from 'utils/axios';
import type { Customer, CustomerKind } from '../types/customers';
export type { Customer, CustomerKind };

export type CustomersListResponse = {
  message: string;
  data: Customer[];
  pagination: { page: number; limit: number; total: number; totalPages: number; hasNext: boolean; hasPrev: boolean };
};

export type ListCustomersParams = {
  search?: string;
  page?: number;
  limit?: number;
  kind?: 'ANY' | CustomerKind;         // ANY (padrão), PERSON, COMPANY
  branch?: 'any' | 'matrix' | 'branch';// só se kind=COMPANY: matrix (matriz), branch (filial)
  includeHierarchy?: boolean;          // pede isMatriz/isFilial/parentCustomerId no payload
};

export type LinkedPerson = {
  id: string;
  customerId: string;
  personId: string;
  role?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

/**
 * NOVO: listagem com filtros por tipo e hierarquia (se o backend suportar).
 * Caso o backend ainda não suporte, você pode temporariamente ignorar as flags
 * (o front continua funcionando para busca 'Todos').
 */
export async function listCustomersAdvanced(params?: ListCustomersParams) {
  const q: any = {
    page: params?.page ?? 1,
    limit: params?.limit ?? 20,
    search: params?.search || undefined,
    kind: params?.kind && params.kind !== 'ANY' ? params.kind : undefined,
    branch: params?.branch && params.branch !== 'any' ? params.branch : undefined,
    includeHierarchy: params?.includeHierarchy ? 1 : undefined
  };
  const { data } = await axios.get<CustomersListResponse>('/customers', { params: q });
  return data;
}

// Helper para ranking e ordenação consistente no cliente
// Aceita clientes com campos extras opcionais (isFilial, name) vindos da API.
export function sortCustomersMatrizFilialPF(
  list: Array<Customer & { isFilial?: boolean; name?: string }>
) {
  const rank = (c: { kind: CustomerKind; isFilial?: boolean }) =>
    c.kind === 'COMPANY' ? (c.isFilial ? 1 : 0) : 2;
  return [...(list || [])].sort((a, b) => {
    const r = rank(a) - rank(b);
    if (r !== 0) return r;
    const an = (a.displayName ?? a.name ?? '').toString();
    const bn = (b.displayName ?? b.name ?? '').toString();
    return an.localeCompare(bn, 'pt-BR', { sensitivity: 'base', ignorePunctuation: true, numeric: true });
  });
}

export async function listCustomers(params?: { search?: string; page?: number; limit?: number }) {
  const q = {
    page: params?.page ?? 1,
    limit: params?.limit ?? 20,
    search: params?.search || undefined
  };
  const { data } = await axios.get<CustomersListResponse>('/customers', { params: q });
  return data;
}

// Aceita string (busca) ou objeto { search, page, limit } e sempre retorna o ARRAY de customers.
export async function listPeople(params?: { search?: string; page?: number; limit?: number; status?: string } | string) {
  const isStr = typeof params === 'string';
  const q = {
    page: !isStr ? params?.page ?? 1 : 1,
    limit: !isStr ? params?.limit ?? 20 : 20,
    search: isStr ? (params as string) : params?.search || undefined,
    status: !isStr ? params?.status : undefined,
  };
  const { data } = await axios.get<CustomersListResponse>('/customers/people', { params: q });
  // @ts-ignore
  return Array.isArray(data) ? (data as unknown as Customer[]) : (data?.data ?? []);
}

export async function listCompanies(params?: { search?: string; page?: number; limit?: number; status?: string } | string) {
  const isStr = typeof params === 'string';
  const q = {
    page: !isStr ? params?.page ?? 1 : 1,
    limit: !isStr ? params?.limit ?? 20 : 20,
    search: isStr ? (params as string) : params?.search || undefined,
    status: !isStr ? params?.status : undefined,
  };
  const { data } = await axios.get<CustomersListResponse>('/customers/companies', { params: q });
  // @ts-ignore
  return Array.isArray(data) ? (data as unknown as Customer[]) : (data?.data ?? []);
}

// Igual a listCompanies, mas preserva a paginação (usado pela tela de listagem, que precisa
// de controles de página — listCompanies() descarta esse dado para não quebrar quem só quer o array).
export async function listCompaniesPage(params?: { search?: string; page?: number; limit?: number; status?: string }) {
  const q = {
    page: params?.page ?? 1,
    limit: params?.limit ?? 25,
    search: params?.search || undefined,
    status: params?.status,
  };
  const { data } = await axios.get<CustomersListResponse>('/customers/companies', { params: q });
  return data;
}

export type LinkedUser = {
  id: string;
  companyId: string;
  userId: string;
  role?: string;
  isPrimary: boolean;
  isLegalRepresentative: boolean;
  createdAt: string;
  updatedAt: string;
  user?: any; // Dados completos do usuário
};

export async function getAccessStatusBulk(customerIds: string[]) {
  if (customerIds.length === 0) return new Set<string>();
  const { data } = await axios.get<{ withAccess: string[] }>('/customers/access-status', {
    params: { ids: customerIds.join(',') }
  });
  return new Set(data?.withAccess ?? []);
}

export type StructureStatus = {
  isBranch: boolean;
  parent: { id: string; displayName: string } | null;
  branches: { id: string; childId: string; displayName: string }[];
};

export async function getStructureStatusBulk(customerIds: string[]) {
  if (customerIds.length === 0) return {} as Record<string, StructureStatus>;
  const { data } = await axios.get<Record<string, StructureStatus>>('/customers/structure-status', {
    params: { ids: customerIds.join(',') }
  });
  return data ?? {};
}

export async function getCompanyUsers(companyId: string) {
  const { data } = await axios.get<{ message?: string; data?: LinkedUser[] } | LinkedUser[]>(
    `/customers/${companyId}/users`
  );
  // aceita wrapper ou payload cru
  // @ts-ignore
  return Array.isArray(data) ? (data as LinkedUser[]) : (data?.data ?? []);
}

export async function linkUserToCompany(companyId: string, userId: string, payload?: any) {
  const { data } = await axios.post<{ message: string; data: LinkedUser }>(`/customers/${companyId}/users`, {
    userId,
    ...payload
  });
  return data.data;
}

export async function resolveSubjectId(subjectId: string) {
  const { data } = await axios.get<{ message: string; data: Customer }>(`/customers/resolve/${subjectId}`);
  return data.data;
}

export type CreateCustomerAccessResult = {
  created: boolean;
  alreadyLinked: boolean;
  userId: string;
  email: string;
  /** Só vem preenchido quando um usuário novo foi criado agora (senha padrão a comunicar ao cliente). */
  temporaryPassword?: string;
};

/**
 * Cria (ou vincula, se já existir) o login do cliente PJ para ele fazer os próprios
 * pedidos: garante a role "Cliente" e o vínculo com a empresa automaticamente.
 */
export async function createCustomerAccess(
  companyId: string,
  payload: { email: string; phone: string }
): Promise<CreateCustomerAccessResult> {
  const { data } = await axios.post<{ message: string; data: CreateCustomerAccessResult }>(
    `/customers/${companyId}/access`,
    payload
  );
  return data.data;
}

/** Sincroniza manualmente um cliente com o Omie — necessário pra sub-clientes B2C, que
 * nunca são enviados automaticamente (ver customer.entity.ts / omie-customer-push.service.ts). */
export async function syncCustomerWithOmie(customerId: string) {
  const { data } = await axios.post<{ message: string }>(`/integrations/omie/customers/${customerId}/sync`);
  return data;
}

export const subjectId = (customer: Customer) => customer.id;

export async function deleteCustomer(id: string) {
  const { data } = await axios.delete<{ message: string }>(`/customers/${id}`);
  return data;
}

// Funções adicionais para CustomerForm
export async function createCustomer(payload: any) {
  const { data } = await axios.post<any>('/customers', payload);
  // backend pode retornar { message, data } ou o objeto direto
  return data?.data ?? data;
}

// Aceita flag 'tree' (true/false) para pedir expansões no backend
// e lida tanto com {message,data} quanto com objeto cru.
export async function getCustomer(id: string, tree?: boolean) {
  const { data } = await axios.get<{ message?: string; data?: any } | any>(`/customers/${id}`, {
    params: tree ? { tree: true } : undefined
  });
  return (data && typeof data === 'object' && 'data' in data) ? (data as any).data : data;
}

export async function updateCustomer(id: string, payload: any) {
  const { data } = await axios.patch<{ message: string; data: any }>(`/customers/${id}`, payload);
  return data.data;
}

export async function updateCustomerCompany(id: string, payload: any) {
  const { data } = await axios.patch<{ message: string; data: any }>(`/customers/${id}/company`, payload);
  return data.data;
}

export async function updateCustomerPerson(id: string, payload: any) {
  const { data } = await axios.patch<{ message: string; data: any }>(`/customers/${id}/person`, payload);
  return data.data;
}

export function extractDigits(value: string): string {
  return value.replace(/\D/g, '');
}

export async function getReceitaFederalData(cnpj: string) {
  const cnpjClean = extractDigits(cnpj);
  // backend pode retornar {message,data} OU o objeto cru
  const { data } = await axios.get<{ message?: string; data?: any } | any>(`/customers/receita-federal/${cnpjClean}`);
  if (data && typeof data === 'object' && 'data' in data) return (data as any).data;
  return data; // objeto cru
}

export function formatCNPJ(cnpj: string): string {
  const digits = extractDigits(cnpj);
  return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
}

export function formatCPF(cpf: string): string {
  const digits = extractDigits(cpf);
  return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
}

export function formatCEP(cep: string): string {
  const digits = extractDigits(cep);
  return digits.replace(/(\d{5})(\d{3})/, '$1-$2');
}

// Funções de endereços
export async function createCustomerAddress(customerId: string, payload: any) {
  const { data } = await axios.post<{ message: string; data: any }>(`/customers/${customerId}/addresses`, payload);
  return data.data;
}

export async function updateCustomerAddress(customerId: string, addressId: string, payload: any) {
  const { data } = await axios.patch<{ message: string; data: any }>(`/customers/${customerId}/addresses/${addressId}`, payload);
  return data.data;
}

export async function deleteCustomerAddress(customerId: string, addressId: string) {
  const { data } = await axios.delete<{ message: string }>(`/customers/${customerId}/addresses/${addressId}`);
  return data;
}

// Funções de filiais
export async function linkAsBranch(parentId: string, childId: string, payload?: any) {
  const { data } = await axios.post<{ message: string; data: any }>(`/customers/${parentId}/branches`, {
    childId,
    ...payload
  });
  return data.data;
}

export async function createCompanyAsBranch(parentId: string, payload: any) {
  const { data } = await axios.post<{ message: string; data: any }>(`/customers/${parentId}/branches`, payload);
  return data.data;
}

export async function deleteCompanyBranch(parentId: string, branchId: string) {
  const { data } = await axios.delete<{ message: string }>(`/customers/${parentId}/branches/${branchId}`);
  return data;
}

export async function getCompanyBranches(companyId: string) {
  const { data } = await axios.get<{ message?: string; data?: any[] } | any[]>(
    `/customers/${companyId}/branches`
  );
  // aceita wrapper ou array cru
  // @ts-ignore
  return Array.isArray(data) ? (data as any[]) : (data?.data ?? []);
}

// Opcional: árvore leve (matriz + filiais). Mantém compat com back atual.
export async function getCompanyTree(companyId: string) {
  // tenta endpoint de árvore via ?tree=true (se existir)
  try {
    const root = await getCustomer(companyId, true);
    return root;
  } catch {
    return { branches: await getCompanyBranches(companyId) };
  }
}

// Funções de pessoas da empresa substituídas pro Users
export async function unlinkUserFromCompany(companyId: string, userId: string) {
  const { data } = await axios.delete<{ message: string }>(`/customers/${companyId}/users/${userId}`);
  return data;
}